import React from 'react';
import { Modal, Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { Formik } from 'formik';
import { createNetwork, updateNetwork } from 'app/crud/networks.crud';

import { successToast, errorToast } from 'utils/toastUtils';
import { isValidEmail } from 'utils/utils';
import { isIP, isMACAddress } from 'validator';
import BeSafeButton from 'components/commons/BeSafeButton';
import BeSafeMultiSelect from 'components/commons/multiselect/BeSafeMultiSelect';
import BeSafeControlFeedback from 'components/commons/BeSafeControlFeedback';

const AddUserModal = ({
  editMode,
  selectedNetwork,
  showModal,
  onHide,
  onCreate,
  onUpdate,
}) => {
  const intl = useIntl();
  let ipv4Error = null;
  let ipv6Error = null;
  let macError = null;

  const initialNetworkValues = {
    name: editMode ? selectedNetwork?.name : '',
    group: editMode ? selectedNetwork?.data?.group : '',
    domain: editMode ? selectedNetwork?.data?.domain : '',
    computer: editMode ? selectedNetwork?.data?.computer : '',
    ipv4Addresses: editMode ? selectedNetwork?.data?.ipv4_addresses : [],
    ipv6Addresses: editMode ? selectedNetwork?.data?.ipv6_addresses : [],
    macAddresses: editMode ? selectedNetwork?.data?.mac_addresses : [],
    email: editMode ? selectedNetwork?.data?.email : '',
    observation_id: editMode ? selectedNetwork?.data?.observation_id : ''
  };

  const validateIPV4 = (value) => {
    if (!value) return true;

    if (value.includes('/')) {
      const [ip, mask] = value.split('/');
      if (mask !== '32') return false;

      return isIP(ip, 4);
    }

    return isIP(value, 4);
  };

  const validateIPV4Field = (value, setFieldError) => {
    ipv4Error = null;
    setFieldError('ipv4Addresses', null);

    if (validateIPV4(value)) {
      return true;
    }
    setFieldError(
      'ipv4Addresses',
      intl.formatMessage({
        id: 'NETWORK_OBJECTS.IPV4_ERROR',
      })
    );
    ipv4Error = intl.formatMessage({
      id: 'NETWORK_OBJECTS.IPV4_ERROR',
    });
    return false;
  };

  const validateIPV6 = (value) => {
    if (!value) return true;

    if (value.includes('/')) {
      const [ip, mask] = value.split('/');
      if (mask !== '128') return false;

      return isIP(ip, 6);
    }

    return isIP(value, 6);
  };

  const validateIPV6Field = (value, setFieldError) => {
    ipv6Error = null;
    setFieldError('ipv6Addresses', null);

    if (validateIPV6(value)) {
      return true;
    }

    setFieldError(
      'ipv6Addresses',
      intl.formatMessage({
        id: 'NETWORK_OBJECTS.IPV6_ERROR',
      })
    );

    ipv6Error = intl.formatMessage({
      id: 'NETWORK_OBJECTS.IPV6_ERROR',
    });
    return false;
  };

  const validateMacField = (value, setFieldError) => {
    macError = null;
    setFieldError('macAddresses', null);

    if (isMACAddress(value) || !value) {
      return true;
    }
    setFieldError(
      'macAddresses',
      intl.formatMessage({
        id: 'NETWORK_OBJECTS.MAC_ERROR',
      })
    );
    macError = intl.formatMessage({
      id: 'NETWORK_OBJECTS.MAC_ERROR',
    });
    return false;
  };

  const formatIPs = (ips, mask) => {
    return ips.map((ip) => {
      if (ip.includes('/')) return ip;
      return ip + '/' + mask;
    });
  };

  const validateForm = (values) => {
    const errors = {};

    if (!values.name || !values.name.trim()) {
      errors.name = intl.formatMessage({ id: 'GENERAL.REQUIRED_FIELD' });
    }

    if (values.name.length > 64) {
      errors.name = intl.formatMessage({
        id: 'GENERAL.MAX_NAME_SIZE',
      });
    }

    if (
      values.ipv4Addresses.length === 0 &&
      values.ipv6Addresses.length === 0
    ) {
      errors.ipv4Addresses = intl.formatMessage({
        id: 'NETWORK_OBJECTS.FIELD_REQUIRED',
      });
      errors.ipv6Addresses = intl.formatMessage({
        id: 'NETWORK_OBJECTS.FIELD_REQUIRED',
      });
    }

    if (!values.email || !values.email.trim()) {
      errors.email = intl.formatMessage({ id: 'GENERAL.REQUIRED_FIELD' });
    }

    if (values.email && isValidEmail(values.email) === false) {
      errors.email = intl.formatMessage({
        id: 'GENERAL.INVALID_FORMAT_EMAIL',
      });
    }

    if (ipv4Error) {
      errors.ipv4Addresses = ipv4Error;
    }

    if (ipv6Error) {
      errors.ipv6Addresses = ipv6Error;
    }

    if (macError) {
      errors.macAddresses = macError;
    }

    if (
      values.observation_id &&
      !/^\d{6}$/.test(values.observation_id)
    ) {
      errors.observation_id = intl.formatMessage({
        id: 'NETWORK_OBJECTS.OBSERVATION_ID_ERROR',
      });
    }

    return errors;
  };

  return (
    <Modal show={showModal} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title>
          {editMode ? (
            <FormattedMessage id="USER_OBJECTS.EDIT" />
          ) : (
            <FormattedMessage id="USER_OBJECTS.ADD" />
          )}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={{
          ...initialNetworkValues,
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);

          let params = {
            name: values.name,
            email: values.email,
            group: values.group,
            domain: values.domain,
            computer: values.computer,
            ipv4_addresses: values.ipv4Addresses,
            ipv6_addresses: values.ipv6Addresses,
            mac_addresses: values.macAddresses,
            source: ['Local'],
            type: 'user',
            observation_id: values.observation_id,
          };

          if (editMode) {
            updateNetwork({ ...params, id: selectedNetwork.id })
              .then((response) => {
                onUpdate(params);
                successToast({
                  body: 'USER_OBJECTS.EDIT_SUCCESS',
                  intl: intl,
                });
              })
              .catch((err) => {
                errorToast({ body: err.response.data?.error, intl: intl });
              })
              .finally(() => {
                setSubmitting(false);
              });
          } else {
            createNetwork(params)
              .then((response) => {
                onCreate();
                successToast({
                  body: 'USER_OBJECTS.ADD_SUCCESS',
                  intl: intl,
                });
              })
              .catch((err) => {
                errorToast({ body: err.response.data?.error, intl: intl });
              })
              .finally(() => {
                setSubmitting(false);
              });
          }
        }}
        validate={validateForm}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldError,
          isSubmitting,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group controlId="name">
                <Form.Label>
                  <FormattedMessage id="GENERAL.NAME" />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  onChange={handleChange}
                  isInvalid={errors.name}
                  className="border-secondary"
                  placeholder={intl.formatMessage({
                    id: 'GENERAL.NAME_PLACEHOLDER',
                  })}
                  value={values.name}
                  required
                />
                {errors.name && touched.name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group controlId="email">
                <Form.Label>
                  <FormattedMessage id="NETWORK_OBJECTS.EMAIL" />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="email"
                  onChange={handleChange}
                  isInvalid={errors.email}
                  className="border-secondary"
                  value={values.email}
                />
                {errors.email && touched.email && (
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group controlId="group">
                <Form.Label>
                  <FormattedMessage id="NETWORK_OBJECTS.GROUP" />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="group"
                  onChange={handleChange}
                  className="border-secondary"
                  placeholder={intl.formatMessage({
                    id: 'NETWORK_OBJECTS.GROUP_PLACEHOLDER',
                  })}
                  value={values.group}
                />
              </Form.Group>
              <Form.Group controlId="domain">
                <Form.Label>
                  <FormattedMessage id="NETWORK_OBJECTS.DOMAIN" />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="domain"
                  onChange={handleChange}
                  className="border-secondary"
                  placeholder={intl.formatMessage({
                    id: 'NETWORK_OBJECTS.DOMAIN_PLACEHOLDER',
                  })}
                  value={values.domain}
                />
              </Form.Group>
              <Form.Group controlId="computer">
                <Form.Label>
                  <FormattedMessage id="NETWORK_OBJECTS.HOSTNAME" />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="computer"
                  onChange={handleChange}
                  className="border-secondary"
                  placeholder={intl.formatMessage({
                    id: 'NETWORK_OBJECTS.HOSTNAME_PLACEHOLDER',
                  })}
                  value={values.computer}
                />
              </Form.Group>
              <Form.Group controlId="observation_id">
                <Form.Label>
                  <FormattedMessage id="NETWORK_OBJECTS.OBSERVATION_ID" />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="observation_id"
                  onChange={handleChange}
                  className="border-secondary"
                  isInvalid={errors.observation_id}
                  placeholder={intl.formatMessage({
                    id: 'NETWORK_OBJECTS.OBSERVATION_ID',
                  })}
                  value={values.observation_id}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.observation_id}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="ipv4Addresses">
                <Form.Label>
                  <FormattedMessage id="OBJECT_EXPLORER.IPV4_LIST" />
                </Form.Label>
                <BeSafeMultiSelect
                  onChange={(items) =>
                    setFieldValue('ipv4Addresses', formatIPs(items, '32'))
                  }
                  selectedItems={values?.ipv4Addresses}
                  placeholder="192.0.2.1"
                  formatName={(str) => str}
                  validate={(value) => validateIPV4Field(value, setFieldError)}
                  limitVisible={false}
                  limit={10}
                  inputClassName="minw-150"
                  inputEnabled
                  disableModal
                  validateEmpty
                />
                {errors.ipv4Addresses && (
                  <BeSafeControlFeedback>
                    {errors.ipv4Addresses}
                  </BeSafeControlFeedback>
                )}
              </Form.Group>
              <Form.Group controlId="ipv6Addresses">
                <Form.Label>
                  <FormattedMessage id="OBJECT_EXPLORER.IPV6_LIST" />
                </Form.Label>
                <BeSafeMultiSelect
                  onChange={(items) =>
                    setFieldValue('ipv6Addresses', formatIPs(items, '128'))
                  }
                  selectedItems={values?.ipv6Addresses}
                  placeholder="2001:db8:3333:4444:5555:6666:7777:8888"
                  formatName={(str) => str}
                  validate={(value) => validateIPV6Field(value, setFieldError)}
                  limitVisible={false}
                  limit={10}
                  inputClassName="minw-300"
                  inputEnabled
                  disableModal
                  validateEmpty
                />
                {errors.ipv6Addresses && (
                  <BeSafeControlFeedback>
                    {errors.ipv6Addresses}
                  </BeSafeControlFeedback>
                )}
              </Form.Group>
              <Form.Group controlId="macAddresses">
                <Form.Label>
                  <FormattedMessage id="OBJECT_EXPLORER.MAC_ADDRESSES" />
                </Form.Label>
                <BeSafeMultiSelect
                  onChange={(items) => setFieldValue('macAddresses', items)}
                  selectedItems={values?.macAddresses}
                  placeholder="00-1A-2B-3C-4D-5E"
                  formatName={(str) => str}
                  validate={(value) => validateMacField(value, setFieldError)}
                  limitVisible={false}
                  limit={10}
                  inputClassName="minw-150"
                  inputEnabled
                  disableModal
                  validateEmpty
                />
                {errors.macAddresses && (
                  <BeSafeControlFeedback>
                    {errors.macAddresses}
                  </BeSafeControlFeedback>
                )}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="py-3">
              <BeSafeButton
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                text={<FormattedMessage id="GENERAL.SAVE" />}
              />
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddUserModal;
